import { z } from "zod";
import { type DoseUnit, type FormularyKind, needsStrength } from "./catalog";

/**
 * Inventory: stock by lot, in the formulary item's dose unit (mg for most
 * medications, mL for compounded mixes, each for bags and supplies). Shown and
 * entered as vials (or bags, or a count).
 */

/** Amount in one vial: strength × vial size (mg units), the vial size (mL units), or 1 (each). Null when unknown. */
export function perVial(unit: DoseUnit, strengthPerMl: number | null, vialSizeMl: number | null): number | null {
  if (unit === "each") return 1;
  if (!vialSizeMl) return null;
  if (unit === "ml") return vialSizeMl;
  return strengthPerMl ? strengthPerMl * vialSizeMl : null;
}

/** Amount for "N vials + M mL" (M ignored for each). Null if it can't be worked out. */
export function amountFor(unit: DoseUnit, strengthPerMl: number | null, vialSizeMl: number | null, vials: number, ml: number): number | null {
  if (unit === "each") return vials;
  const vial = vials ? perVial(unit, strengthPerMl, vialSizeMl) : 0;
  const perMl = unit === "ml" ? 1 : strengthPerMl;
  if (vial === null || (ml && !perMl)) return null;
  return round3(vials * vial + ml * (perMl ?? 0));
}

export const round3 = (n: number) => Math.round(n * 1000) / 1000;

/** Default shelf colours, picked from the name so each medication keeps its own. */
export const VIAL_COLORS = ["#C2185B", "#1E7F5C", "#2B6CB0", "#B7791F", "#6B46C1", "#C05621", "#2C7A7B", "#9B2C2C", "#4A5568", "#D53F8C", "#2F855A", "#3182CE", "#805AD5", "#DD6B20", "#319795", "#744210"];
export function vialColor(name: string, color: string | null): string {
  if (color) return color;
  let h = 0;
  for (const ch of name.toLowerCase()) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return VIAL_COLORS[h % VIAL_COLORS.length]!;
}

/** Days before the BUD when a lot is marked "expiring soon". */
export const EXPIRING_DAYS = 30;

export type LotStatus = "ok" | "expiring" | "expired" | "no_date";

export interface InventoryLot {
  id: string;
  lotNumber: string;
  expiresOn: string | null;
  status: LotStatus;
  strengthPerMl: number | null;
  vialSizeMl: number | null;
  /** Amount in one vial; null when the vial size (or strength) isn't known. */
  perVial: number | null;
  receivedAmount: number;
  remaining: number;
  opening: boolean;
  receivedAt: string;
  receivedBy: string;
  purchaseLineId: string | null;
}

export interface InventoryItem {
  formularyItemId: string;
  name: string;
  kind: FormularyKind;
  doseUnit: DoseUnit;
  color: string;
  active: boolean;
  /** Defaults for receiving: the last delivery, else the formulary. */
  strengthPerMl: number | null;
  vialSizeMl: number | null;
  /** Lots with stock (or below zero), soonest BUD first; expired last. */
  lots: InventoryLot[];
  /** On hand and not expired. */
  usable: number;
  expired: number;
  /** A lot went below zero: more was charted than was on record. */
  countOff: boolean;
  /** A starting-count lot hasn't been checked yet (lot number, BUD, vial size). */
  needsDetails: boolean;
  /** Low stock at or below this many vials (bags, or items); null = no alert. */
  reorderAt: number | null;
  /** Usable stock is at or below the reorder level. */
  low: boolean;
}

export interface InventoryShelf {
  today: string;
  items: InventoryItem[];
}

export const CHANGE_REASONS = ["received", "opening", "charted", "wasted", "expired", "correction"] as const;
export type ChangeReason = (typeof CHANGE_REASONS)[number];
export const CHANGE_REASON_LABELS: Record<ChangeReason, string> = {
  received: "Received",
  opening: "Opening count",
  charted: "Charted",
  wasted: "Wasted",
  expired: "Expired",
  correction: "Count correction",
};

export interface InventoryChange {
  id: string;
  at: string;
  reason: ChangeReason;
  amount: number;
  lotId: string | null;
  lotNumber: string | null;
  chartId: string | null;
  note: string;
  by: string;
}

export interface InventoryItemDetail extends InventoryItem {
  /** Every lot, including used-up ones. */
  allLots: InventoryLot[];
  history: InventoryChange[];
  /** Purchases of this item, newest first (to link a starting count to its order). Managers only. */
  purchases: PurchaseLineSummary[];
}

/** A purchase line of one item, for picking which order a lot came from. */
export interface PurchaseLineSummary {
  id: string;
  supplier: string;
  date: string | null;
  invoiceNumber: string;
  orderNumber: string;
  lotNumber: string;
  expiresOn: string | null;
  strengthPerMl: number | null;
  vialSizeMl: number | null;
  quantity: number;
}

const lotNumber = z.string().trim().max(60).default("");
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .default(null);
const positive = z.number().positive().max(1_000_000);

export const newProductSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
    drugName: z.string().trim().max(160).default(""),
    kind: z.enum(["medication", "fluid", "supply"]),
    doseUnit: z.enum(["mg", "mcg", "iu", "units", "ml", "each"]),
    strengthPerMl: positive.nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (needsStrength(v.doseUnit) && v.strengthPerMl === null) ctx.addIssue({ code: "custom", path: ["strengthPerMl"], message: "Enter the strength per mL" });
  });


/** Amount as vials + mL (mL ignored for items counted each). */
const amountParts = { vials: z.number().min(0).max(100_000).default(0), ml: z.number().min(0).max(100_000).default(0) };

export const removeSchema = z.object({
  reason: z.enum(["wasted", "expired"]),
  ...amountParts,
  note: z.string().trim().max(500).default(""),
});

export const countSchema = z.object({ ...amountParts, note: z.string().trim().max(500).default("") });

/** Stock counted on the shelf that isn't on record yet (go-live count, or vials from a past purchase). */
export const stockOnHandSchema = z.object({
  ...amountParts,
  lotNumber,
  expiresOn: isoDate,
  strengthPerMl: positive.nullable().default(null),
  vialSizeMl: positive.nullable().default(null),
  purchaseLineId: z.string().uuid().nullable().default(null),
  note: z.string().trim().max(500).default(""),
});

export const lotEditSchema = z.object({
  lotNumber,
  expiresOn: isoDate,
  strengthPerMl: positive.nullable().default(null),
  vialSizeMl: positive.nullable().default(null),
  /** The purchase line this lot came from (links a starting count to its order). */
  /** Left out: keep the current link. */
  purchaseLineId: z.string().uuid().nullable().optional(),
});

/**
 * Lots on hand offered on a chart for one medication: medications' open vials first (soonest BUD,
 * then least left), then unopened lots soonest BUD first. Bags and supplies: soonest BUD first.
 */
export interface ChartLotOption {
  id: string;
  lotNumber: string;
  expiresOn: string | null;
  strengthPerMl: number | null;
  status: LotStatus;
  /** A vial has been started (what's left isn't a whole number of vials). */
  open: boolean;
}

/** What's left isn't a whole number of vials: one has been opened. False when the vial size isn't known. */
export function hasOpenVial(remaining: number, perVialAmount: number | null): boolean {
  if (!perVialAmount || remaining <= 0) return false;
  const frac = remaining / perVialAmount - Math.floor(remaining / perVialAmount);
  return frac > 1e-6 && frac < 1 - 1e-6;
}

/** Whole vials and the fraction of the open one, for drawing. */
export function vialsOf(remaining: number, perVialAmount: number | null): { full: number; partial: number } | null {
  if (!perVialAmount || remaining <= 0) return remaining <= 0 ? { full: 0, partial: 0 } : null;
  const n = remaining / perVialAmount;
  const full = Math.floor(n + 1e-9);
  return { full, partial: Math.round((n - full) * 100) / 100 };
}

/** "3 ¼ vials", "1 vial", "12 each". */
export function formatVials(remaining: number, perVialAmount: number | null, unit: DoseUnit, kind: FormularyKind): string {
  if (unit === "each") return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(remaining)} ${kind === "fluid" ? (remaining === 1 ? "bag" : "bags") : "on hand"}`;
  if (!perVialAmount) return "vial size not set";
  const n = Math.round((remaining / perVialAmount) * 100) / 100;
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n)} ${n === 1 ? "vial" : "vials"}`;
}

// ── Purchases ───────────────────────────────────────────────────────────

export interface Supplier {
  id: string;
  name: string;
  phone: string;
  website: string;
  accountNumber: string;
  notes: string;
  active: boolean;
}

export const supplierInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: z.string().trim().max(40).default(""),
  website: z.string().trim().max(200).default(""),
  accountNumber: z.string().trim().max(60).default(""),
  notes: z.string().trim().max(500).default(""),
  active: z.boolean().default(true),
});

const cents = z.number().int().min(0).max(100_000_000);
const text60 = z.string().trim().max(60).default("");

export const purchaseAttachmentSchema = z.object({
  key: z.string().min(1).max(300),
  name: z.string().trim().max(200),
  contentType: z.string().max(100),
});
export type PurchaseAttachment = z.infer<typeof purchaseAttachmentSchema>;

export const purchaseHeaderSchema = z.object({
  supplierId: z.string().uuid(),
  orderNumber: text60,
  invoiceNumber: text60,
  orderedOn: isoDate,
  receivedOn: isoDate,
  shippingCents: cents.default(0),
  /** Promo codes and other discounts (a positive amount, taken off). */
  discountCents: cents.default(0),
  taxCents: cents.default(0),
  /** Surcharges and handling fees (fuel, refrigeration, …). */
  feesCents: cents.default(0),
  /** As charged; null if not known. */
  totalCents: cents.nullable().default(null),
  notes: z.string().trim().max(1000).default(""),
  /** The invoice or order sheet, as uploaded. */
  attachments: z.array(purchaseAttachmentSchema).max(10).default([]),
});

const lineFields = {
  formularyItemId: z.string().uuid().nullable().default(null),
  /** As the supplier names it. */
  productName: z.string().trim().max(200).default(""),
  /** The supplier's product ID or NDC. */
  supplierProductId: text60,
  strengthPerMl: positive.nullable().default(null),
  vialSizeMl: positive.nullable().default(null),
  quantity: z.number().positive().max(100_000),
  unitCents: cents.nullable().default(null),
  lotNumber,
  expiresOn: isoDate,
};

export const purchaseLineInputSchema = z
  .object({
    ...lineFields,
    /** A product not in the formulary yet (added to it). */
    newProduct: newProductSchema.nullable().default(null),
  })
  .refine((l) => !(l.formularyItemId && l.newProduct), { message: "Pick one product" })
  .refine((l) => l.formularyItemId || l.newProduct || l.productName, { message: "Pick a product or name it" });

export const purchaseInputSchema = purchaseHeaderSchema.extend({
  /** Put formulary items on the shelf as new lots (off for history). */
  addToStock: z.boolean().default(true),
  lines: z.array(purchaseLineInputSchema).min(1).max(80),
});
export type PurchaseInput = z.infer<typeof purchaseInputSchema>;

/** Editing a line later: lot and BUD changes also update its lot on the shelf. */
export const purchaseLineEditSchema = z.object({ ...lineFields, purchaseId: z.string().uuid() });

export interface PurchaseLineView {
  id: string;
  formularyItemId: string | null;
  /** Formulary name, else the supplier's name for it. */
  name: string;
  productName: string;
  supplierProductId: string;
  strengthPerMl: number | null;
  vialSizeMl: number | null;
  quantity: number;
  unitCents: number | null;
  lotNumber: string;
  expiresOn: string | null;
  /** A lot on the shelf came from this line. */
  lotId: string | null;
  /**
   * What each one really cost: its price plus its share of shipping, fees, and tax,
   * less discounts (split by line amount). Null without a price. Use this for cost per drip.
   */
  landedUnitCents: number | null;
}

export interface PurchaseView {
  id: string;
  supplierId: string;
  supplier: string;
  orderNumber: string;
  invoiceNumber: string;
  orderedOn: string | null;
  receivedOn: string | null;
  shippingCents: number;
  discountCents: number;
  taxCents: number;
  feesCents: number;
  totalCents: number | null;
  notes: string;
  attachments: PurchaseAttachment[];
  /** Sum of the lines (quantity × unit price, where known). */
  linesCents: number;
  /** Shipping + fees + tax − discounts (or total − lines, when every line has a price), spread over the lines. */
  extrasCents: number;
  lines: PurchaseLineView[];
}

/**
 * The amount spread over a purchase's lines to get landed cost. When the total charged is
 * known and every line has a price, it's total − lines (catches anything not itemized);
 * otherwise shipping + fees + tax − discounts.
 */
export function purchaseExtrasCents(p: { shippingCents: number; discountCents: number; taxCents: number; feesCents: number; totalCents: number | null }, linesCents: number, allPriced: boolean): number {
  if (p.totalCents !== null && allPriced && linesCents > 0) return p.totalCents - linesCents;
  return p.shippingCents + p.feesCents + p.taxCents - p.discountCents;
}

/** A line's landed unit cost, in cents (fractional; round for display). */
export function landedUnitCents(unitCents: number | null, linesCents: number, extrasCents: number): number | null {
  if (unitCents === null) return null;
  if (linesCents <= 0) return unitCents;
  return Math.max(0, unitCents * (1 + extrasCents / linesCents));
}

/** Reports → Purchases. Dated by order date (else received date). */
export interface PurchasesReport {
  from: string;
  to: string;
  lines: {
    date: string;
    supplier: string;
    orderNumber: string;
    invoiceNumber: string;
    name: string;
    productName: string;
    supplierProductId: string;
    strengthPerMl: number | null;
    vialSizeMl: number | null;
    quantity: number;
    unitCents: number | null;
    totalCents: number | null;
    /** Price plus its share of shipping, fees, and tax, less discounts. */
    landedUnitCents: number | null;
    landedTotalCents: number | null;
    lotNumber: string;
    expiresOn: string | null;
  }[];
  bySupplier: { supplier: string; orders: number; linesCents: number; shippingCents: number; discountCents: number; taxCents: number; feesCents: number; totalCents: number }[];
  /** totalCents: at invoice prices; landedCents: what it really cost. */
  byItem: { name: string; quantity: number; totalCents: number; landedCents: number }[];
}
